import React, { useEffect } from 'react'

import { routes, useFlag } from '@sketch/modules-common'

// Components
import {
  Dropdown,
  LoadingPlaceholder,
  useModalContext,
  ToastLink,
  SelectDropdownSubMenu,
} from '@sketch/components'
import {
  MoveShareToProjectModal,
  RemoveShareFromProjectModal,
} from 'modules/projects'
import {
  Chevron,
  SubMenuItem,
  SubMenuTrigger,
} from './ManageProjectAndCollectionActions.styles'

// Hooks
import {
  useAddSharesToCollection,
  useRemoveSharesFromCollection,
  useGetCollectionsForSelectLazy,
} from 'modules/collections/operations'
import { useToast } from '@sketch/toasts'

// GraphQL
import {
  useGetShareCollectionLazyQuery,
  CollectionForSelectFragment,
  ProjectFragment,
  ShareListItemFragment,
} from '@sketch/gql-types'

// Utils
import { ErrorHandler } from '@sketch/tracing'

// Icons
import { ReactComponent as PlusIcon } from '@sketch/icons/plus-16'
import { ReactComponent as ExclamationMark } from '@sketch/icons/exclamation-mark-16'
import { CreateCollectionModal } from 'modules/collections/modals'

interface Share
  extends Pick<
    ShareListItemFragment,
    | 'identifier'
    | 'name'
    | 'project'
    | 'userCanUpdateSettings'
    | 'publicAccessLevel'
    | 'type'
  > {}

const MoveToProjectAction: React.FC<{ share: Share }> = ({ share }) => {
  const { showModal } = useModalContext()
  return (
    <Dropdown.Item
      onClick={() =>
        showModal(MoveShareToProjectModal, {
          share,
          currentProject: share.project,
        })
      }
    >
      Move To&hellip;
    </Dropdown.Item>
  )
}

const RemoveFromProjectAction: React.FC<{ share: Share }> = ({ share }) => {
  const { showModal } = useModalContext()
  return (
    <Dropdown.Item
      onClick={() => showModal(RemoveShareFromProjectModal, { share })}
    >
      Remove from Project&hellip;
    </Dropdown.Item>
  )
}

const SingleLevelActions: React.FC<{ share: Share }> = ({ share }) => (
  <>
    <MoveToProjectAction share={share} />
    {share.project && <RemoveFromProjectAction share={share} />}
  </>
)

export const CollectionsLoadingState = () => (
  <SubMenuItem
    text="Loading collections…"
    icon={({ className }) => (
      <LoadingPlaceholder className={className} size="16px" />
    )}
    disabled
  />
)

export const CollectionsErrorState = () => (
  <SubMenuItem
    text="Unable to load collections"
    icon={({ className }) => <ExclamationMark className={className} />}
    disabled
  />
)

interface MoveSubMenuProps {
  share: Share
  project: ProjectFragment
  workspaceIdentifier: string
  currentCollection?: CollectionForSelectFragment | null
}

type FetchCollectionState = 'loading' | 'error'

const MoveSubMenu: React.FC<MoveSubMenuProps> = ({
  share,
  project,
  workspaceIdentifier,
  currentCollection,
}) => {
  const { showModal } = useModalContext()
  const [
    getCollections,
    { loading, error, data },
  ] = useGetCollectionsForSelectLazy()
  let collections: (CollectionForSelectFragment | FetchCollectionState)[] = [
    'loading',
  ]

  if (loading) {
    collections = ['loading']
  } else if (error) {
    collections = ['error']
  } else {
    // Exclude the share's current collection if it exists
    collections = data.filter(
      ({ identifier }) => identifier !== currentCollection?.identifier
    )
  }

  return (
    <SelectDropdownSubMenu
      value="Move"
      usePortal={true}
      breakpoint="sm"
      renderTrigger={props => (
        <SubMenuTrigger text="Move" additional={<Chevron />} {...props} />
      )}
      items={['PROJECT', 'COLLECTION']}
      renderItem={(item, props) => {
        if (item === 'PROJECT') {
          return (
            <SubMenuItem
              text="To Project…"
              onClick={() => {
                showModal(MoveShareToProjectModal, {
                  share,
                  currentProject: share.project,
                })
              }}
            />
          )
        }

        return (
          <div
            onMouseEnter={() => {
              getCollections({
                variables: { projectIdentifier: project.identifier },
              })
            }}
          >
            <MoveToCollectionSubMenu
              share={share}
              project={project}
              workspaceIdentifier={workspaceIdentifier}
              collections={collections}
              currentCollection={currentCollection}
            />
          </div>
        )
      }}
    />
  )
}

interface RemoveSubMenuProps {
  share: Share
  project: ProjectFragment
  collection: CollectionForSelectFragment
  workspaceIdentifier: string
}

const RemoveSubMenu: React.FC<RemoveSubMenuProps> = ({
  share,
  project,
  collection,
  workspaceIdentifier,
}) => {
  const { showModal } = useModalContext()
  const { showToast } = useToast()

  const removeSharesFromCollection = useRemoveSharesFromCollection({
    projectIdentifier: project.identifier,
    onCompleted: () => {
      showToast(
        <>
          Document moved to &ldquo;
          <ToastLink
            to={routes.WORKSPACE_PROJECT.create({
              projectId: project.identifier,
              workspaceId: workspaceIdentifier,
            })}
          >
            {project.name}
          </ToastLink>
          &rdquo;
        </>
      )
    },
  })

  return (
    <SelectDropdownSubMenu
      value="Remove"
      usePortal={true}
      breakpoint="sm"
      renderTrigger={props => (
        <SubMenuTrigger text="Remove" additional={<Chevron />} {...props} />
      )}
      items={['PROJECT', 'COLLECTION']}
      renderItem={(item, props) => {
        if (item === 'PROJECT') {
          return (
            <SubMenuItem
              text="From Project…"
              onClick={() =>
                showModal(RemoveShareFromProjectModal, { share, collection })
              }
            />
          )
        }

        return (
          <SubMenuItem
            text="From Collection"
            onClick={() => {
              removeSharesFromCollection({
                variables: {
                  identifier: collection.identifier,
                  shareIdentifiers: [share.identifier],
                },
              })
            }}
          />
        )
      }}
    />
  )
}

interface MoveToCollectionSubMenuProps {
  share: Share
  project: ProjectFragment
  workspaceIdentifier: string
  collections: (CollectionForSelectFragment | FetchCollectionState)[]
  currentCollection?: CollectionForSelectFragment | null
}

const MoveToCollectionSubMenu: React.FC<MoveToCollectionSubMenuProps> = ({
  share,
  project,
  workspaceIdentifier,
  collections,
  currentCollection,
}) => {
  const { showToast } = useToast()
  const { showModal } = useModalContext()

  const addSharesToCollection = useAddSharesToCollection({
    projectIdentifier: project.identifier,
    currentCollectionIdentifier: currentCollection?.identifier,
    onCompleted: ({ addSharesToCollection }) => {
      const { collection } = addSharesToCollection

      showToast(
        <>
          Document added to &quot;
          <ToastLink
            to={routes.WORKSPACE_COLLECTION.create({
              workspaceId: workspaceIdentifier,
              projectId: project.identifier,
              collectionId: collection.identifier,
            })}
          >
            {collection.name}
          </ToastLink>
          &quot; collection
        </>
      )
    },
  })

  const handleCreateCollection = () => {
    showModal(CreateCollectionModal, {
      project,
      workspaceIdentifier,
      shareIdentifier: share.identifier,
    })
  }

  const headerItem =
    collections.length === 0 ? (
      <SubMenuItem text="No collections available" disabled />
    ) : null

  return (
    <SelectDropdownSubMenu
      headerItem={headerItem}
      value="To Collection"
      usePortal={true}
      breakpoint="sm"
      renderTrigger={props => (
        <SubMenuItem text="To Collection" additional={<Chevron />} {...props} />
      )}
      footerItem={
        <SubMenuItem
          onClick={handleCreateCollection}
          text="Create Collection…"
          icon={PlusIcon}
        />
      }
      items={collections}
      renderItem={(item, props) => {
        if (item === 'loading') {
          return <CollectionsLoadingState />
        } else if (item === 'error') {
          return <CollectionsErrorState />
        }

        return (
          <SubMenuItem
            text={item.name}
            onClick={() => {
              addSharesToCollection({
                variables: {
                  identifier: item.identifier,
                  shareIdentifiers: [share.identifier],
                },
              })
            }}
          />
        )
      }}
    />
  )
}

interface ManageProjectAndCollectionActionsProps {
  share: Share
  workspaceIdentifier: string
}

const ManageProjectAndCollectionActions: React.FC<ManageProjectAndCollectionActionsProps> = ({
  share,
  workspaceIdentifier,
}) => {
  // TODO: remove 'collections' feature flag
  // ISSUE: https://github.com/sketch-hq/Cloud/issues/15999
  const collectionsEnabled = useFlag('collections')

  const [
    getCollection,
    { data, error, loading },
  ] = useGetShareCollectionLazyQuery()

  const shareIdentifier = share.identifier

  useEffect(() => {
    if (collectionsEnabled) {
      getCollection({ variables: { shareIdentifier } })
    }
  }, [getCollection, collectionsEnabled, shareIdentifier])

  if (!collectionsEnabled) {
    return <SingleLevelActions share={share} />
  }

  if (loading) {
    const loadingCount = share.project ? 2 : 1
    return <Dropdown.Skeleton count={loadingCount} />
  }

  if (error) {
    ErrorHandler.shouldNeverHappen(
      'Fetching share.collection should never fail'
    )

    return <SingleLevelActions share={share} />
  }

  const maybeProject = share.project
  const maybeCollection = data?.share?.collection

  if (maybeProject) {
    return (
      <>
        <MoveSubMenu
          share={share}
          project={maybeProject}
          workspaceIdentifier={workspaceIdentifier}
          currentCollection={maybeCollection}
        />
        {maybeCollection ? (
          <RemoveSubMenu
            share={share}
            project={maybeProject}
            collection={maybeCollection}
            workspaceIdentifier={workspaceIdentifier}
          />
        ) : (
          <RemoveFromProjectAction share={share} />
        )}
      </>
    )
  }

  return <SingleLevelActions share={share} />
}

export default ManageProjectAndCollectionActions
